import { Link } from 'react-router-dom';
import { MessageCircleIcon } from 'lucide-react';
import { RoutineCard } from '../shared/RoutineCard';
import { Faq } from '../shared/Faq';
import { SubscriptionBlock } from '../shared/SubscriptionBlock';
import { HeadingText } from '../shared/HeadingText';
import { PageHero } from '../shared/PageHero';
import { shopFaq } from '../../data/faq';
import { useCatalog } from '../../contexts/CatalogContext';
import { useOverlayActions } from '../../contexts/StoreContext';
import { founder, salon } from '../../data/content';
import { SALON_CODE } from '../../data/codes';
import { usePages } from '../../contexts/SiteContext';
import { fill } from '../../utils/copy';
import { mediaOr } from '../../core/rules/media';
import { shopLabels } from '../../data/shopLabels';
import { btnOnClay, btnPrimaryLg, btnSecondaryLg } from '../../ui/styles';
import { closingHeading, sectionHeading } from '../../ui/sections';
import { plural } from '../../core/format/text';
import { RoutineCompare } from './RoutineCompare';
import { RoutineAnchor } from './RoutineAnchor';
import { GiftTierList } from './GiftTierList';
import { anchorRoutine, ROUTINES_FAQ_COUNT } from './routineRules';

/**
 * Protocollen: de pagina met de hoogste orderwaarde. Volgorde = de vragen van een bezoeker
 * (docs/VORMGEVING.md §3): wat is een protocol (hero, sand) · hoe werkt het en wat krijg ik
 * erbij (shell) · het complete protocol (blush) · of begin bij je klacht, en vergelijk (sand) ·
 * abonnement (shell) · wat als het niet past (sand) · al klant in de salon? (klei).
 */
export function Routines() {
  const pages = usePages();
  const {
    openQuiz
  } = useOverlayActions();
  const {
    routines,
    routineTotals
  } = useCatalog();
  const copy = pages.routines;
  const complete = anchorRoutine(routines, r => routineTotals(r).price);
  const others = routines.filter(r => r.id !== complete?.id);
  const heroMedia = mediaOr(copy.hero.media, {
    image: complete?.image || founder.withProducts,
    alt: complete ? `De producten van ${complete.name} op een rij` : `${founder.name} met de producten waar ze mee werkt`
  });
  return <>
      {/* Hero: zelfde opbouw als salon en over. In het beeld: wie het opstelt, en wat je meekrijgt. */}
      <PageHero titleId="routines-title" eyebrow={copy.hero.eyebrow ? fill(copy.hero.eyebrow) : undefined} title={<HeadingText heading={copy.hero.heading} accentBlock data-id="element-8337" data-mp="20x814ijum5mv" />} body={copy.hero.body ? fill(copy.hero.body) : undefined} media={heroMedia} byline={{
      lead: <>
              Persoonlijk huidplan, opgesteld door <span className="text-ink" data-id="element-8338" data-mp="2emtdha357mlx">{founder.firstName}</span>
            </>,
      sub: `${plural(routines.length, 'protocol', 'protocollen')} · 100% vegan`
    }} actions={(copy.hero.cta || copy.hero.secondaryCta) && <>
              {copy.hero.cta && <button type="button" onClick={openQuiz} aria-haspopup="dialog" className={btnPrimaryLg} data-id="element-8339" data-mp="2tlnc8tzv03sx">
                  {fill(copy.hero.cta)}
                </button>}
              {copy.hero.secondaryCta && <Link to="/webshop" className={btnSecondaryLg} data-id="element-8340" data-mp="cwuk46ztp3f5">
                  {fill(copy.hero.secondaryCta)}
                </Link>}
            </>} data-id="element-8336" data-mp="1vvscyhmdjj09" />

      {/* Hoe het werkt: eerst uitleggen, dan pas aanbieden. Met de cadeaus ernaast. */}
      <section className="bg-shell py-20 sm:py-24" aria-labelledby="routines-how" data-id="element-8341" data-mp="10mf4mubmk8qj">
        <div className="mx-auto grid max-w-content gap-14 px-6 lg:grid-cols-12 lg:gap-16 lg:px-10" data-id="element-8342" data-mp="vpons4rzx942">
          <div className="lg:col-span-8" data-id="element-8343" data-mp="qwvjtj4cq4t1">
            <h2 id="routines-how" className="font-display text-[1.9rem] font-light leading-tight text-ink sm:text-[2.25rem]" data-id="element-8344" data-mp="qvke3drcimza">
              Zo werkt een protocol
            </h2>
            <ol className="mt-8 grid gap-8 sm:grid-cols-3 sm:gap-6" data-id="element-8345" data-mp="1jfixwekxkjkm">
              {copy.steps.map((s, i) => <li key={s.title} className="border-t border-ink/15 pt-5" data-id="element-8346" data-mp="1elerpxjhofkl" data-mpi={i}>
                  {/* De lijst is al genummerd voor schermlezers; de volgorde is hier de inhoud. */}
                  <span aria-hidden="true" className="font-display text-[2rem] font-light leading-none text-clay" data-id="element-8347" data-mp="1465tkkzici6y">
                    {i + 1}
                  </span>
                  <h3 className="mt-4 font-display text-xl leading-snug text-ink" data-id="element-8348" data-mp="u5lnn511mpie">{fill(s.title)}</h3>
                  <p className="mt-2 text-[15px] leading-relaxed text-muted" data-id="element-8349" data-mp="1hvpr9ce8ep9e">{fill(s.body)}</p>
                </li>)}
            </ol>
          </div>

          <div className="lg:col-span-4" data-id="element-8350" data-mp="1mwx1rj7re2jr">
            <h2 className="font-display text-[1.9rem] font-light leading-tight text-ink sm:text-[2.25rem]" data-id="element-8351" data-mp="1854vrxtuu0up">{fill(copy.gifts.heading)}</h2>
            <GiftTierList className="mt-8" data-id="element-8352" data-mp="1d36doffjnu4l" />
            <a href={salon.whatsapp} target="_blank" rel="noreferrer" className="mt-4 flex items-start gap-2 text-[14px] leading-relaxed text-muted underline-offset-4 transition-colors duration-150 ease-soft hover:text-ink hover:underline" data-id="element-8353" data-mp="20ubv2z14uuh0">
              <MessageCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-clay" strokeWidth={1.5} aria-hidden="true" data-id="element-8354" data-mp="3lbuq21vd1p2g" />
              {fill(copy.gifts.note)}
              <span className="sr-only" data-id="element-8355" data-mp="3y7eeff03pzt">{shopLabels.viaWhatsApp}</span>
            </a>
          </div>
        </div>
      </section>

      {complete && <RoutineAnchor routine={complete} data-id="element-8356" data-mp="37e64ertgydwc" />}

      {/* Of begin bij je klacht. Met maar één protocol online is er geen "overige": dan alleen vergelijken (vanaf twee). */}
      {(others.length > 0 || routines.length > 1) && <section className="bg-sand py-20 sm:py-24 lg:py-32" aria-labelledby={others.length > 0 ? 'other-routines' : undefined} data-id="element-8357" data-mp="3gfclb2015qq4">
          <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8358" data-mp="vnkazpfenjlb">
            {others.length > 0 && <>
                <div className="grid gap-6 lg:grid-cols-12 lg:items-end lg:gap-16" data-id="element-8359" data-mp="rmi910u3stfn">
                  <h2 id="other-routines" className={`${sectionHeading} text-ink lg:col-span-7`} data-id="element-8360" data-mp="3kgfon9b0lo8v">
                    <HeadingText heading={copy.others.heading} data-id="element-8361" data-mp="3pfk24c4z2850" />
                  </h2>
                  {copy.others.body && <p className="max-w-md text-[17px] leading-relaxed text-muted lg:col-span-5" data-id="element-8362" data-mp="3uewo6uo0rb6j">{fill(copy.others.body)}</p>}
                </div>

                <ul className={`mt-14 grid gap-6 md:grid-cols-2 ${others.length >= 3 ? 'lg:grid-cols-3' : ''}`} data-id="element-8363" data-mp="2aav8if0ut8s">
                  {others.map(r => <li key={r.id} className="h-full bg-shell p-7 lg:p-8" data-id="element-8364" data-mp="36oj2dlw128ke">
                      <RoutineCard routine={r} data-id="element-8365" data-mp="36mojed4bgqiy" />
                    </li>)}
                </ul>
              </>}

            <RoutineCompare heading={fill(copy.compareHeading)} data-id="element-8366" data-mp="3fie8aw4z4313" />

            <p className="mt-12 max-w-2xl text-[16px] leading-relaxed text-muted" data-id="element-8367" data-mp="2izq2xfg75il1">
              Liever met één product beginnen? Dat kan altijd. Je vindt alles los in de{' '}
              <Link to="/webshop" className="text-ink underline underline-offset-4" data-id="element-8368" data-mp="2myyz0qyplsjr">
                webshop
              </Link>
              . Twijfel je, dan{' '}
              <button type="button" onClick={openQuiz} aria-haspopup="dialog" className="text-ink underline underline-offset-4" data-id="element-8369" data-mp="2ajr37fofqlwx">
                kijk ik even mee
              </button>
              .
            </p>
          </div>
        </section>}

      <SubscriptionBlock tone="shell" data-id="element-8370" data-mp="1wieot8oxasd5" />

      <section className="bg-sand py-20 sm:py-24 lg:py-32" data-id="element-8371" data-mp="1wjgas87h8z9n">
        <div className="mx-auto max-w-content px-6 lg:px-10" data-id="element-8372" data-mp="1rs80zb28jbvy">
          <Faq items={shopFaq.slice(0, ROUTINES_FAQ_COUNT)} title={fill(copy.faqTitle)} data-id="element-8373" data-mp="1rpvauambpv7t" />
        </div>
      </section>

      {/* Voor klanten van de salon: het ene klei-vlak van deze pagina */}
      <section className="bg-clay py-20 text-shell sm:py-24" aria-labelledby="salon-client" data-id="element-8374" data-mp="1it4hi5z5ougx">
        <div className="mx-auto grid max-w-content gap-8 px-6 lg:grid-cols-12 lg:items-end lg:gap-16 lg:px-10" data-id="element-8375" data-mp="1drf5an076ar9">
          <div className="lg:col-span-8" data-id="element-8376" data-mp="18v6u1kp4f1pd">
            <h2 id="salon-client" className={closingHeading} data-id="element-8377" data-mp="2wp9zwwqdu5g3">
              <HeadingText heading={copy.salonClient.heading} accentClassName="text-blush" data-id="element-8378" data-mp="2wnyzn6w5bftq" />
            </h2>
            {copy.salonClient.body && <p className="mt-4 max-w-2xl text-[17px] leading-relaxed text-shell" data-id="element-8379" data-mp="2npn36o8llntd">{fill(copy.salonClient.body, {
              code: SALON_CODE
            })}</p>}
          </div>
          {copy.salonClient.cta && <div className="lg:col-span-4 lg:flex lg:justify-end" data-id="element-8380" data-mp="2dysd18s8e42n">
              <a href={salon.whatsapp} target="_blank" rel="noreferrer" className={btnOnClay} data-id="element-8381" data-mp="2dxh998yscwbj">
                {fill(copy.salonClient.cta)}
                <span className="sr-only" data-id="element-8382" data-mp="24zf20bogl7ft">{shopLabels.viaWhatsApp}</span>
              </a>
            </div>}
        </div>
      </section>
    </>;
}